import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import Header from "@/components/Header";
import { fetchApplicationStatus, fetchHostEvent } from "@/lib/api";
import { toast } from "sonner";

const FIGTREE = '"Figtree", "Inter", system-ui, sans-serif';
const INTER = '"Inter", "Figtree", system-ui, sans-serif';
const ACTIVICA_PIXEL =
    '"FT Activica Pixel", "FT Activica Normal", monospace';

const STATUS_COPY = {
    received: {
        label: "Application Received",
        accent: "#FFFCE1",
        body: "We've got your application. Our team reads every one. If you're shortlisted, we'll reach out by email to schedule a short call.",
    },
    shortlisted: {
        label: "Shortlisted",
        accent: "#07E6F6",
        body: "You're through to the next step. Look out for an email from the Emergent team to schedule a short call.",
    },
    selected: {
        label: "Selected",
        accent: "#90FDA6",
        body: "You're the Vibecity Lead for your city. Check your inbox for the onboarding pack.",
    },
    closed: {
        label: "Closed",
        accent: "#9A9A92",
        body: "This cycle has closed. We'll keep your application on file for the next cohort.",
    },
};

const HOST_STATUS_COPY = {
    received: {
        label: "Application Received",
        accent: "#FFFCE1",
        body: "We've got your host application. We review new events within 3 business days.",
    },
    under_review: {
        label: "Under Review",
        accent: "#07E6F6",
        body: "Your event is being reviewed by the Emergent team. Hang tight - we'll reach out with next steps.",
    },
    approved: {
        label: "Approved",
        accent: "#90FDA6",
        body: "You're a Vibecity host. Check your inbox for the playbook, brand kit and credit access.",
    },
    declined: {
        label: "Not This Time",
        accent: "#9A9A92",
        body: "This event wasn't the right fit right now. Feel free to apply again with a new pitch.",
    },
};

const isHostRef = (ref) => /^VC-H-/i.test(String(ref || "").trim());

export default function Status() {
    const { refId } = useParams();
    const navigate = useNavigate();
    const [value, setValue] = useState(refId || "");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = "Check Status · Vibecity";
        if (refId) {
            lookup(refId);
        }
        // eslint-disable-next-line
    }, [refId]);

    const lookup = async (id) => {
        const clean = (id || "").trim().toUpperCase();
        if (!clean) {
            toast("Enter your reference ID.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Route by ref-ID prefix - hosts use VC-H-XXXXX, leads use VC-XXXXXX.
            const data = isHostRef(clean)
                ? { ...(await fetchHostEvent(clean)), _type: "host" }
                : await fetchApplicationStatus(clean);
            setResult(data);
        } catch (e) {
            setResult(null);
            if (e?.response?.status === 404) {
                setError("We couldn't find an application with that ID.");
            } else {
                setError("Something went wrong. Try again in a minute.");
            }
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        navigate(`/check/${value.trim().toUpperCase()}`);
        lookup(value);
    };

    const status = result
        ? (result._type === "host" ? HOST_STATUS_COPY : STATUS_COPY)[result.status] || {
              label: result.status,
              accent: "#FFFCE1",
              body: "We'll update this status as your application moves through review.",
          }
        : null;

    return (
        <main
            data-testid="status-page"
            className="relative"
            style={{ background: "#0E100F" }}
        >
            <Header />

            <div className="relative" style={{ paddingTop: 80 }}>
                {/* LEFT — fixed hero image (matches /apply layout) */}
                <div
                    data-testid="status-left"
                    className="hidden lg:block"
                    style={{
                        position: "fixed",
                        top: 80,
                        left: 0,
                        width: "50%",
                        height: "calc(100vh - 80px)",
                        zIndex: 1,
                    }}
                >
                    <img
                        src="/assets/form-image.jpg"
                        alt="Vibecity"
                        className="w-full h-full"
                        style={{
                            objectFit: "cover",
                            objectPosition: "center",
                        }}
                    />
                </div>

                {/* RIGHT — status lookup panel */}
                <div
                    data-testid="status-right"
                    className="relative px-6 md:px-12 lg:px-16 xl:px-20 py-14 md:py-20 lg:ml-[50%]"
                    style={{
                        background: "#1F1F1F",
                        minHeight: "calc(100vh - 80px)",
                    }}
                >
                    <div className="max-w-[640px] mx-auto">
                        <h1
                            style={{
                                fontFamily: FIGTREE,
                                fontWeight: 800,
                                fontSize: "clamp(2.4rem, 4.6vw, 4rem)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                color: "#F7F4DA",
                            }}
                        >
                            Where your application stands.
                        </h1>
                        <p
                            className="mt-3"
                            style={{
                                fontFamily: INTER,
                                fontWeight: 400,
                                fontSize: "clamp(15px, 1.1vw, 18px)",
                                lineHeight: 1.5,
                                color: "#9A9A92",
                            }}
                        >
                            Enter the reference ID we sent you. It looks like{" "}
                            <span
                                style={{
                                    fontFamily: ACTIVICA_PIXEL,
                                    color: "#F7F4DA",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                VC-XXXXXX
                            </span>
                            .
                        </p>

                        {/* Lookup form */}
                        <form
                            onSubmit={onSubmit}
                            data-testid="status-form"
                            className="mt-10 md:mt-14"
                        >
                            <label
                                htmlFor="ref-id"
                                style={{
                                    display: "block",
                                    fontFamily: INTER,
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: "#C9C9BB",
                                    marginBottom: 8,
                                }}
                            >
                                Reference ID
                                <span style={{ color: "#FF4D4F", marginLeft: 4 }}>
                                    *
                                </span>
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    id="ref-id"
                                    data-testid="status-input"
                                    className="apply-input flex-1"
                                    placeholder="VC-XXXXXX"
                                    value={value}
                                    onChange={(e) =>
                                        setValue(e.target.value.toUpperCase())
                                    }
                                    autoCapitalize="characters"
                                    style={{
                                        fontFamily: ACTIVICA_PIXEL,
                                        letterSpacing: "0.06em",
                                        fontSize: 16,
                                    }}
                                />
                                <button
                                    type="submit"
                                    data-testid="status-submit"
                                    disabled={loading}
                                    className="hover:brightness-105 transition-[filter] disabled:opacity-60"
                                    style={{
                                        padding: "14px 28px",
                                        background:
                                            "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                                        boxShadow:
                                            "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                                        borderRadius: 10,
                                        fontFamily: FIGTREE,
                                        fontWeight: 700,
                                        fontSize: 14,
                                        letterSpacing: "0.05em",
                                        textTransform: "uppercase",
                                        color: "#0E100F",
                                        border: 0,
                                        cursor: loading ? "wait" : "pointer",
                                    }}
                                >
                                    {loading ? "Checking…" : "Check"}
                                </button>
                            </div>
                        </form>

                        {/* Result panel */}
                        <div className="mt-10">
                            {!result && !error && !loading && (
                                <EmptyState />
                            )}
                            {error && <ErrorState message={error} />}
                            {result && status && (
                                <ResultState result={result} status={status} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

function EmptyState() {
    return (
        <div
            data-testid="status-empty"
            className="rounded-xl p-6 md:p-8"
            style={{
                background: "#161616",
                border: "1px solid #2E2E2E",
            }}
        >
            <p
                style={{
                    fontFamily: INTER,
                    fontWeight: 400,
                    fontSize: 15,
                    color: "#9A9A92",
                    lineHeight: 1.5,
                }}
            >
                Enter your reference ID above to see where your application
                sits in review.
            </p>
            <Link
                to="/apply"
                data-testid="status-empty-apply-link"
                className="inline-flex items-center mt-5 hover:opacity-80 transition-opacity"
                style={{
                    fontFamily: FIGTREE,
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#F7F4DA",
                    textDecoration: "none",
                }}
            >
                Don't have one yet? Apply →
            </Link>
        </div>
    );
}

function ErrorState({ message }) {
    return (
        <div
            data-testid="status-error"
            className="rounded-xl p-6 md:p-8"
            style={{
                background: "#161616",
                border: "1px solid #4A2A2A",
            }}
        >
            <div
                style={{
                    fontFamily: FIGTREE,
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: "#FF7A7A",
                    marginBottom: 8,
                }}
            >
                Not found
            </div>
            <p
                style={{
                    fontFamily: INTER,
                    fontWeight: 400,
                    fontSize: 16,
                    color: "#E6E6D8",
                    lineHeight: 1.45,
                }}
            >
                {message}
            </p>
            <Link
                to="/apply"
                className="inline-flex items-center mt-5 hover:opacity-80 transition-opacity"
                style={{
                    fontFamily: FIGTREE,
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#F7F4DA",
                    textDecoration: "none",
                }}
            >
                Apply to lead your city →
            </Link>
        </div>
    );
}

function ResultState({ result, status }) {
    return (
        <div
            data-testid="status-result"
            className="rounded-xl p-6 md:p-8"
            style={{
                background: "#161616",
                border: "1px solid #2E2E2E",
            }}
        >
            {/* Reference ID */}
            <div
                style={{
                    fontFamily: FIGTREE,
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: "#7A7A72",
                    marginBottom: 8,
                }}
            >
                Reference ID
            </div>
            <div
                style={{
                    fontFamily: ACTIVICA_PIXEL,
                    fontWeight: 400,
                    fontSize: "clamp(2rem, 3.4vw, 2.8rem)",
                    letterSpacing: "0.04em",
                    lineHeight: 1,
                    color: "#F7F4DA",
                    wordBreak: "break-all",
                }}
            >
                {result.reference_id}
            </div>

            <hr
                style={{
                    border: 0,
                    borderTop: "1px solid #2A2A2A",
                    margin: "24px 0",
                }}
            />

            {/* Status pill */}
            <div className="flex items-center gap-3 mb-3">
                <span
                    aria-hidden
                    style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        background: status.accent,
                        boxShadow: `0 0 12px ${status.accent}88`,
                    }}
                />
                <span
                    style={{
                        fontFamily: FIGTREE,
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: status.accent,
                    }}
                >
                    {status.label}
                </span>
            </div>
            <p
                style={{
                    fontFamily: INTER,
                    fontWeight: 400,
                    fontSize: 15,
                    color: "#C9C9BB",
                    lineHeight: 1.5,
                    margin: 0,
                }}
            >
                {status.body}
            </p>

            <hr
                style={{
                    border: 0,
                    borderTop: "1px solid #2A2A2A",
                    margin: "24px 0",
                }}
            />

            {/* Applicant meta */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <MetaCell label="Applicant" value={result.full_name} />
                <MetaCell label="City" value={result.city} />
            </div>
        </div>
    );
}

function MetaCell({ label, value }) {
    return (
        <div>
            <div
                style={{
                    fontFamily: FIGTREE,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: "#7A7A72",
                    marginBottom: 4,
                }}
            >
                {label}
            </div>
            <div
                style={{
                    fontFamily: INTER,
                    fontWeight: 500,
                    fontSize: 15,
                    color: "#F7F4DA",
                }}
            >
                {value || "—"}
            </div>
        </div>
    );
}
